import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { CourseTemplate } from 'app/library/course-offering/models/course-template.model';
import { Module } from 'app/library/module/models/module.model';
import { PasswordlessAccess } from 'app/library/passwordless-access/models/passwordless-access.model';
import { ScheduledJobId } from 'app/library/scheduler/enums/scheduledJob-id.enum';
import { AddScheduleToSection, RemoveScheduleFromSection, UpdateScheduleInSection } from 'app/library/section/actions';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { ScheduleSubjectTextDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/schedule-subject-text/schedule-subject-text.component';
import { AddScheduleToModule, RemoveScheduleFromModule, UpdateScheduleInModule } from '../../module/actions/index';
import { Section } from '../../section/models/section.model';
import { SheduledJobEntityType } from '../enums/scheduledJob-entity-type.enum';
import { ScheduledJobStatus } from '../enums/scheduledJob-status.enum';
import { ScheduledJobType } from '../enums/scheduledJob-type.enum';
import { ScheduledJob } from '../models/scheduled-job.model';
import { SchedulerApiService } from './scheduler-api-service';
import { CourseOffering } from 'app/library/course-offering/models/course-offering.model';

@Injectable()
export class SchedulerService {
    private _showScheduleSetSnackBar(): void {
        const successMessage = this._translateService.instant('MODULE.SCHEDULER.MESSAGE.SCHEDULER_SET');

        this._matSnackBar.open(successMessage, 'OK', {
            duration: SnackBarTime.fast,
            verticalPosition: 'top',
        });
    }

    constructor(private _schedulerApiService: SchedulerApiService, private _translateService: TranslateService, private _matSnackBar: MatSnackBar, private _store: Store) {}

    deleteScheduledJob(
        scheduledJob: ScheduledJob,
        dialogRef: MatDialogRef<ScheduleSubjectTextDialogComponent>,
        entity: Section | Module | CourseOffering,
        showSnackBar = true
    ): void {
        this._schedulerApiService.deleteScheduledJob(scheduledJob.id).then(() => {
            dialogRef.close();

            switch (scheduledJob.entityTypeShort) {
                case SheduledJobEntityType.Section:
                    this._store.dispatch(new RemoveScheduleFromSection({ scheduledJobId: scheduledJob.id, entity: entity as Section }));
                    break;
                case SheduledJobEntityType.Module:
                    this._store.dispatch(new RemoveScheduleFromModule({ scheduledJobId: scheduledJob.id, entity: entity as Module }));
                    break;
            }

            if (showSnackBar) {
                const successMessage = this._translateService.instant('MODULE.SCHEDULER.MESSAGE.SCHEDULER_DELETE');

                this._matSnackBar.open(successMessage, 'OK', {
                    duration: SnackBarTime.fast,
                    verticalPosition: 'top',
                });
            }
        });
    }

    updateScheduledJob(data: any, scheduledJob: ScheduledJob, entity: Section | Module | CourseOffering, scheduledJobType: ScheduledJobType): void {
        this._schedulerApiService
            .updateScheduledJob(
                scheduledJob.id,
                Object.assign(
                    new ScheduledJob(),
                    {},
                    {
                        entityId: entity.id,
                        type: scheduledJobType,
                    }
                ),
                data.executionTimestamp,
                {
                    subject: data.subject,
                    message: data.message,
                    entityId: entity.id,
                    notify: data.notify,
                    node: data.node,
                }
            )
            .then((scheduledJob) => {
                switch (scheduledJob.entityTypeShort) {
                    case SheduledJobEntityType.Section:
                        this._store.dispatch(new UpdateScheduleInSection({ scheduledJob, entity: entity as Section }));
                        break;
                    case SheduledJobEntityType.Module:
                        this._store.dispatch(new UpdateScheduleInModule({ scheduledJob, entity: entity as Module }));
                        break;
                }

                this._showScheduleSetSnackBar();
            });
    }

    createScheduledJob(data: any, entity: Section | Module | PasswordlessAccess | CourseOffering, scheduledJobId: ScheduledJobId, scheduledJobType: ScheduledJobType): void {
        this._schedulerApiService
            .createScheduledJob(
                scheduledJobId,
                Object.assign(
                    new ScheduledJob(),
                    {},
                    {
                        entityId: entity.id,
                        type: scheduledJobType,
                    }
                ),
                data.executionTimestamp,
                {
                    subject: data.subject,
                    message: data.message,
                    entityId: entity.id,
                    notify: data.notify,
                    node: data.node,
                }
            )
            .then((scheduledJob) => {
                switch (scheduledJob.entityTypeShort) {
                    case SheduledJobEntityType.Section:
                        this._store.dispatch(new AddScheduleToSection({ scheduledJob, entity: entity as Section }));
                        break;
                    case SheduledJobEntityType.Module:
                        this._store.dispatch(new AddScheduleToModule({ scheduledJob, entity: entity as Module }));
                        break;
                }

                this._showScheduleSetSnackBar();
            });
    }

    async getPendingScheduledJob(entity: Section | Module | CourseTemplate | CourseOffering | PasswordlessAccess, scheduledJobType: ScheduledJobType): Promise<ScheduledJob> {
        let scheduledJob: ScheduledJob;

        if (entity.schedules?.length) {
            entity.schedules.map((schedule) => {
                if (schedule && schedule.type === scheduledJobType && schedule.info.status === ScheduledJobStatus.Pending) {
                    scheduledJob = schedule;
                }
            });
        }

        return scheduledJob;
    }
}
